import { FC } from 'react';

import classNames from 'classnames';
import { BannerImageCropType } from 'utils/constants/banner';
import { conversion } from 'utils/conversion';

import cropModalCancelUpscaleButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/upscale/crop_modal_cancel_upscale_button_click';
import { Button, Card, Loader, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';

import { formatToReactComponent } from 'bloko/common/trl';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { UpscaleCancelConfirmModal } from 'components/common/BannerImg/UpscaleCancelConfirmModal/UpscaleCancelConfirmModal';

import { useSelector } from 'store/utils';

import styles from 'components/common/BannerImg/CropImageModal/ImageContent/UpscaleLoader/UpscaleLoader.module.less';

const TrlKeys = {
    upscaleLoading: {
        part0: 'BannerImg.CropImageModal.ImageContent.upscaleLoading.0',
        part1: 'BannerImg.CropImageModal.ImageContent.upscaleLoading.1',
    },
    upscaleLoadingWaiting: {
        part0: 'BannerImg.CropImageModal.ImageContent.upscaleLoadingWaiting.0',
        part1: 'BannerImg.CropImageModal.ImageContent.upscaleLoadingWaiting.1',
    },
    minutes: {
        zero: 'common.minutesWaiting.zero',
        one: 'common.minutesWaiting.one',
        some: 'common.minutesWaiting.some',
        many: 'common.minutesWaiting.many',
    },
    cancel: 'BannerImg.CropImageModal.ImageContent.UpscaleLoader.cancel',
};

interface UpscaleLoaderProps {
    cropType: BannerImageCropType;
    isFullUpscale?: boolean;
}

export const UpscaleLoader: FC<UpscaleLoaderProps> = ({ cropType, isFullUpscale }) => {
    const aiImageQueueId = useSelector((state) => state.createBanner.images[cropType].aiImageQueueId);
    const waitingTimeMs = useSelector(
        (state) => state.createBanner.images[cropType].aiImageQueuePosition?.waitingTimeMs
    );
    const [
        isUpscaleCancelConfirmModalVisible,
        setIsUpscaleCancelConfirmModalVisibleOn,
        setIsUpscaleCancelConfirmModalVisibleOff,
    ] = useOnOffState(false);

    const waitingTimeMinutes = waitingTimeMs ? Math.round(waitingTimeMs / 60000) : null;

    const trls = useTrls();
    const { isMobile } = useBreakpoint();

    const cancelUpscale = () => {
        cropModalCancelUpscaleButtonClick({ buttonName: 'cancel' });
        setIsUpscaleCancelConfirmModalVisibleOn();
    };

    return (
        <Card
            borderRadius={isFullUpscale ? 20 : undefined}
            borderWidth="default"
            stretched
            padding={12}
            paddingTop={isFullUpscale ? 84 : 12}
            paddingBottom={isFullUpscale ? 84 : 12}
        >
            <div className={classNames(styles.contentWrapper, { [styles.singleCrop]: !isFullUpscale })}>
                <div className={classNames(styles.content, { [styles.singleCropContent]: !isFullUpscale })}>
                    <Loader size={32} />
                    <VSpacing default={isFullUpscale ? 32 : 16} />
                    {aiImageQueueId && (
                        <>
                            {waitingTimeMinutes && waitingTimeMinutes >= 2 ? (
                                <>
                                    <Text
                                        style={isFullUpscale ? 'secondary' : 'primary'}
                                        typography={
                                            isFullUpscale || isMobile ? 'paragraph-2-regular' : 'label-4-regular'
                                        }
                                    >
                                        {trls(TrlKeys.upscaleLoadingWaiting.part0)}
                                    </Text>
                                    <Text
                                        style={isFullUpscale ? 'secondary' : 'primary'}
                                        typography={
                                            isFullUpscale || isMobile ? 'paragraph-2-regular' : 'label-4-regular'
                                        }
                                    >
                                        {formatToReactComponent(trls(TrlKeys.upscaleLoadingWaiting.part1), {
                                            '[0]': waitingTimeMinutes,
                                            '[1]': (
                                                <>
                                                    {conversion(waitingTimeMinutes, {
                                                        zero: trls(TrlKeys.minutes.zero),
                                                        one: trls(TrlKeys.minutes.one),
                                                        some: trls(TrlKeys.minutes.some),
                                                        many: trls(TrlKeys.minutes.many),
                                                    })}
                                                </>
                                            ),
                                        })}
                                    </Text>
                                </>
                            ) : (
                                <>
                                    <Text
                                        style={isFullUpscale ? 'secondary' : 'primary'}
                                        typography={
                                            isFullUpscale || isMobile ? 'paragraph-2-regular' : 'label-4-regular'
                                        }
                                    >
                                        {trls(TrlKeys.upscaleLoading.part0)}
                                    </Text>
                                    <Text
                                        style={isFullUpscale ? 'secondary' : 'primary'}
                                        typography={
                                            isFullUpscale || isMobile ? 'paragraph-2-regular' : 'label-4-regular'
                                        }
                                    >
                                        {trls(TrlKeys.upscaleLoading.part1)}
                                    </Text>
                                </>
                            )}
                            <VSpacing default={isFullUpscale ? 24 : 40} />
                            <Button
                                stretched
                                mode="secondary"
                                style="neutral"
                                onClick={cancelUpscale}
                                size={isFullUpscale || isMobile ? 'medium' : 'small'}
                                data-qa="upscale-cancel"
                            >
                                {trls(TrlKeys.cancel)}
                            </Button>
                        </>
                    )}
                </div>
            </div>
            <UpscaleCancelConfirmModal
                isUpscaleCancelConfirmModalVisible={isUpscaleCancelConfirmModalVisible}
                hideModal={setIsUpscaleCancelConfirmModalVisibleOff}
            />
        </Card>
    );
};
